import { z } from 'zod';
import { AddressSchema, EmailSchema, PhoneSchema, TimestampsSchema } from './common.js';
import { PartyRoleEnum } from './property.js';
// =============================================================================
// Contact Schema - Unified Buyer/Seller with tenant isolation
// =============================================================================
/**
 * Contact types
 */
export const ContactTypeEnum = z.enum(['BUYER', 'SELLER', 'VENDOR', 'OTHER']);
/**
 * Property relationship roles
 */
export const PropertyRoleEnum = z.enum([
    'PRIMARY_BUYER',
    'CO_BUYER',
    'PRIMARY_SELLER',
    'CO_SELLER',
    'BUYER',
    'SELLER',
]);
/**
 * Property relationship
 */
export const PropertyRelationshipSchema = z.object({
    propertyId: z.string(),
    role: PropertyRoleEnum,
    addedAt: z.coerce.date().optional(),
});
/**
 * Contact note
 */
export const ContactNoteSchema = z.object({
    _id: z.string().optional(),
    text: z.string().min(1),
    createdAt: z.coerce.date(),
    createdBy: z.string().optional(), // User ID
});
/**
 * Full Contact schema (database document)
 */
export const ContactSchema = z
    .object({
    _id: z.string(),
    organizationId: z.string(), // TENANT KEY - REQUIRED
    createdBy: z.string().optional(), // Agent who created this contact (for multi-agent orgs)
    // Type
    type: ContactTypeEnum,
    // Identity
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: EmailSchema,
    phone: PhoneSchema.optional(),
    // Address (optional for contacts)
    address: AddressSchema.optional(),
    // Relationships
    relatedPropertyIds: z.array(PropertyRelationshipSchema).default([]),
    // Co-buyers/Co-sellers (self-reference to other contacts)
    linkedContactIds: z.array(z.string()).default([]),
    // Notes
    notes: z.array(ContactNoteSchema).default([]),
    // Status
    isActive: z.boolean().default(true),
})
    .merge(TimestampsSchema);
/**
 * Contact creation input
 */
export const CreateContactInputSchema = z.object({
    type: ContactTypeEnum,
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: EmailSchema,
    phone: PhoneSchema.optional(),
    address: AddressSchema.optional(),
    propertyId: z.string().optional(), // Optionally link to a property on creation
    propertyRole: PropertyRoleEnum.optional(),
});
/**
 * Find or create contact input
 * Used when adding recipients to DocuSign or linking existing contacts to properties
 * If contact with email exists in org, returns existing; otherwise creates new
 */
export const FindOrCreateContactInputSchema = z.object({
    type: ContactTypeEnum,
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    email: EmailSchema,
    phone: PhoneSchema.optional(),
    // Optional: immediately link to property in same atomic operation
    propertyId: z.string().optional(),
    propertyRole: PartyRoleEnum.optional(),
});
/**
 * Contact update input
 */
export const UpdateContactInputSchema = z.object({
    firstName: z.string().min(1).optional(),
    lastName: z.string().min(1).optional(),
    email: EmailSchema.optional(),
    phone: PhoneSchema.optional(),
    address: AddressSchema.partial().optional(),
    type: ContactTypeEnum.optional(),
});
/**
 * Add note input
 */
export const AddNoteInputSchema = z.object({
    text: z.string().min(1, 'Note text is required'),
});
/**
 * Link contact input (for co-buyers/co-sellers)
 */
export const LinkContactInputSchema = z.object({
    linkedContactId: z.string(),
});
/**
 * Associate property input
 */
export const AssociatePropertyInputSchema = z.object({
    propertyId: z.string(),
    role: PropertyRoleEnum,
});
/**
 * Contact list query parameters
 */
export const ContactListQuerySchema = z.object({
    type: ContactTypeEnum.optional(),
    search: z.string().optional(),
    propertyId: z.string().optional(),
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
/**
 * Contact response (safe for client)
 */
export const ContactResponseSchema = ContactSchema;
/**
 * Contact with linked contacts populated
 */
export const ContactWithLinkedSchema = ContactSchema.extend({
    linkedContacts: z.array(z.object({
        _id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
        type: ContactTypeEnum,
    })),
});
//# sourceMappingURL=contact.js.map